// client/src/hooks/useTeamSync.ts
//
// Fire-and-forget sync de stats al dashboard del coach.
// Solo corre si el usuario es miembro activo de un team con sync_enabled = true.
//
// Diseño:
//  - Recibe HistoryStats (counts crudos del parsing in-browser).
//  - Construye el payload con los campos de la whitelist del API.
//  - Envía el JWT de Supabase en el header — el API lo verifica server-side.
//  - Nunca bloquea el flow del import.

import { useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';
import { HistoryStats } from '../utils/pokerStats/types';
import { TeamContext } from './useTeam';

const API_BASE = process.env.REACT_APP_API_URL || '';

export interface TeamSyncInput {
  stats:          HistoryStats;
  handsCount:     number;
  dateRangeStart: string | null;   // YYYY-MM-DD
  dateRangeEnd:   string | null;
  stakes:         string | null;
}

export interface TeamSyncResult {
  synced:     boolean;
  rateLimited: boolean;
  error:      string | null;
}

// ─── Construcción del payload ────────────────────────────────────────────────
// Mapea HistoryStats → shape esperado por api/teams/sync.js
// Solo counts crudos (opp + count), nunca percentages.
// Las claves deben estar en la ALLOWED_PAYLOAD_KEYS del API.

function buildTeamStatsPayload(stats: HistoryStats): Record<string, unknown> {
  const p: Record<string, unknown> = {};

  const opp = stats.handsPlayed;
  if (opp === 0) return p;

  p.vpip         = { opp, count: stats.vpipCount };
  p.pfr          = { opp, count: stats.pfrCount };
  p.threebet     = { opp: stats.threebetOpp, count: stats.threebetCount };
  p.foldTo3bet   = { opp: stats.foldTo3betOpp, count: stats.foldTo3betCount };
  p.ats          = { opp: stats.atsOpp, count: stats.atsCount };

  // AF: bets + raises / calls (solo incluir si hay suficientes calls para el ratio)
  p.af = {
    bets:   stats.afBets,
    raises: stats.afRaises,
    calls:  stats.afCalls,
  };

  // Allin split (Fase 2)
  if (stats.foldTo3betAllinOpp !== undefined) {
    p.foldTo3betAllin    = { opp: stats.foldTo3betAllinOpp,    count: stats.foldTo3betAllinCount ?? 0 };
    p.foldTo3betNonAllin = { opp: stats.foldTo3betNonAllinOpp ?? 0, count: stats.foldTo3betNonAllinCount ?? 0 };
  }

  // CBet flop/turn
  p.cbetFlop        = { opp: stats.cbetFlopOpp,  count: stats.cbetFlopCount };
  p.cbetFlopSuccess = { opp: stats.cbetFlopOpp,  count: stats.cbetFlopSuccessCount };
  p.cbetTurn        = { opp: stats.cbetTurnOpp,  count: stats.cbetTurnCount };
  p.cbetTurnSuccess = { opp: stats.cbetTurnOpp,  count: stats.cbetTurnSuccessCount };

  // Defense
  p.foldToCbetFlop = { opp: stats.foldToCbetFlopOpp, count: stats.foldToCbetFlopCount };
  p.foldToCbetTurn = { opp: stats.foldToCbetTurnOpp, count: stats.foldToCbetTurnCount };
  p.callCbetFlop   = { opp: stats.foldToCbetFlopOpp, count: stats.callCbetFlopCount };
  p.callCbetTurn   = { opp: stats.foldToCbetTurnOpp, count: stats.callCbetTurnCount };

  // Donk
  p.donkFlop  = { opp: stats.donkFlopOpp,  count: stats.donkFlopCount };
  p.donkTurn  = { opp: stats.donkTurnOpp,  count: stats.donkTurnCount };
  p.donkRiver = { opp: stats.donkRiverOpp, count: stats.donkRiverCount };

  // Probe (turn + river)
  if (stats.probeTurnOpp > 0) {
    p.probeTurn  = { opp: stats.probeTurnOpp,  count: stats.probeTurnCount };
  }
  if (stats.probeRiverOpp > 0) {
    p.probeRiver = { opp: stats.probeRiverOpp, count: stats.probeRiverCount };
  }

  // Showdown
  p.wtsd = { opp: stats.flopsSeen,  count: stats.wtsdCount };
  p.wwsf = { opp: stats.wwsfOpp,    count: stats.wwsfCount };
  p.wsd  = { opp: stats.wsdOpp,     count: stats.wsdCount };

  // Matchups posicionales (Fase 1 + Fase 4)
  if (stats.cbetFlopMatchups && Object.keys(stats.cbetFlopMatchups).length > 0) {
    p.cbetFlopMatchups = stats.cbetFlopMatchups;
  }
  if (stats.threebetByMatchup && Object.keys(stats.threebetByMatchup).length > 0) {
    p.threebetByMatchup = stats.threebetByMatchup;
  }
  if (stats.foldTo3betByMatchup && Object.keys(stats.foldTo3betByMatchup).length > 0) {
    p.foldTo3betByMatchup = stats.foldTo3betByMatchup;
  }

  // RFI + VPIP por posición (Fase 3 + Fase C)
  if (stats.rfiByPosition && Object.keys(stats.rfiByPosition).length > 0) {
    p.rfiByPosition = stats.rfiByPosition;
  }
  if (stats.vpipByPosition && Object.keys(stats.vpipByPosition).length > 0) {
    p.vpipByPosition = stats.vpipByPosition;
  }

  // CBet por textura HU + multiway (Fase B + B.2)
  if (stats.cbetFlopByTexture && Object.keys(stats.cbetFlopByTexture).length > 0) {
    p.cbetFlopByTexture = stats.cbetFlopByTexture;
  }
  if (stats.cbetFlopByTextureMulti && Object.keys(stats.cbetFlopByTextureMulti).length > 0) {
    p.cbetFlopByTextureMulti = stats.cbetFlopByTextureMulti;
  }

  // Push/Fold (Fase A)
  if (stats.pushFoldByPosition && Object.keys(stats.pushFoldByPosition).length > 0) {
    p.pushFoldByPosition = stats.pushFoldByPosition;
  }

  // Fold to push (Fase D)
  if (stats.foldToPushByShoverStack && Object.keys(stats.foldToPushByShoverStack).length > 0) {
    p.foldToPushByStack = stats.foldToPushByShoverStack;
  }

  // CBet en pozo 3beteado (Fase E)
  if (stats.cbet3BetPotOpp !== undefined && stats.cbet3BetPotOpp > 0) {
    p.cbet3betPot = {
      opp:    stats.cbet3BetPotOpp,
      count:  stats.cbet3BetPotCount ?? 0,
      ipOpp:  stats.cbet3BetPotIPOpp   ?? 0,
      ipCount:  stats.cbet3BetPotIPCount  ?? 0,
      oopOpp: stats.cbet3BetPotOOPOpp  ?? 0,
      oopCount: stats.cbet3BetPotOOPCount ?? 0,
    };
  }

  return p;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTeamSync(user: User | null, teams: TeamContext[]) {
  const syncStats = useCallback(
    async (input: TeamSyncInput): Promise<TeamSyncResult> => {
      const defaultResult: TeamSyncResult = { synced: false, rateLimited: false, error: null };

      if (!SUPABASE_ENABLED || !user || teams.length === 0) return defaultResult;

      // Buscar teams activos con sync habilitado
      const syncableTeams = teams.filter(
        c => c.membership.status === 'active' && c.membership.syncEnabled
      );

      if (syncableTeams.length === 0) return defaultResult;

      // Obtener el JWT del usuario para autenticarse en el API
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) return { ...defaultResult, error: 'No hay sesión activa' };

      const payload = buildTeamStatsPayload(input.stats);

      // Sync en todos los teams activos (normalmente solo 1 en MVP)
      const results = await Promise.allSettled(
        syncableTeams.map(ctx =>
          fetch(`${API_BASE}/api/teams/sync`, {
            method:  'POST',
            headers: {
              'Content-Type':  'application/json',
              'Authorization': `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              teamId:         ctx.team.id,
              handsCount:     input.handsCount,
              dateRangeStart: input.dateRangeStart,
              dateRangeEnd:   input.dateRangeEnd,
              stakes:         input.stakes,
              statsPayload:   payload,
            }),
          }).then(async res => {
            if (res.status === 429) return { ok: false, rateLimited: true };
            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              return { ok: false, error: data.error || `HTTP ${res.status}` };
            }
            return { ok: true };
          })
        )
      );

      let anyRateLimited = false;
      let anyError: string | null = null;
      let anySynced = false;

      for (const r of results) {
        if (r.status === 'fulfilled') {
          if (r.value.rateLimited) anyRateLimited = true;
          else if (r.value.ok) anySynced = true;
          else if (r.value.error) anyError = r.value.error as string;
        } else {
          anyError = r.reason?.message || 'Error de red';
        }
      }

      if (!anySynced && anyError) {
        console.warn('[useTeamSync]', anyError);
      }

      return { synced: anySynced, rateLimited: anyRateLimited, error: anyError };
    },
    [user, teams]
  );

  return { syncStats };
}
